import type { UserInfoResponse } from '@/api/types';

/**
 * Sesión normal del portal, limitada a la pestaña actual.
 *
 * Se usa sessionStorage (no localStorage) para que una recarga, un deploy o un full-reload de
 * Vite no expulsen al usuario. La sesión desaparece al cerrar la pestaña y nunca se restaura si
 * el JWT ya venció. El backend sigue siendo quien valida firma, audiencia y autorización.
 */
const STORAGE_KEY = 'tilde-portal-session-v1';

export interface StoredPortalSession {
  token: string;
  user: UserInfoResponse;
  tenantId: string;
  expiresAt: number;
}

function readJwtExpiration(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const parsed = JSON.parse(window.atob(padded)) as { exp?: unknown };
    return typeof parsed.exp === 'number' && Number.isFinite(parsed.exp)
      ? parsed.exp * 1000
      : null;
  } catch {
    return null;
  }
}

function isValidUser(value: unknown): value is UserInfoResponse {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<UserInfoResponse>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.email === 'string' &&
    typeof candidate.name === 'string' &&
    typeof candidate.role === 'string' &&
    typeof candidate.tenantId === 'string' &&
    typeof candidate.tenantName === 'string'
  );
}

export function persistPortalSession(
  token: string,
  user: UserInfoResponse,
  expiresInSeconds: number,
): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const jwtExpiration = readJwtExpiration(token);
  const responseExpiration =
    Number.isFinite(expiresInSeconds) && expiresInSeconds > 0
      ? now + expiresInSeconds * 1000
      : null;
  const expiresAt = jwtExpiration ?? responseExpiration;

  // Sin vencimiento verificable no persistimos credenciales. El login actual sigue funcionando
  // en memoria, pero una respuesta incompleta no puede transformarse en una sesión durable.
  if (expiresAt == null || expiresAt <= now) return;

  const session: StoredPortalSession = {
    token,
    user,
    tenantId: user.tenantId,
    expiresAt,
  };

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Safari/modo privado puede bloquear storage. La sesión continúa en memoria.
  }
}

export function restorePortalSession(): StoredPortalSession | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredPortalSession>;
    const valid =
      typeof parsed.token === 'string' &&
      parsed.token.length > 0 &&
      typeof parsed.tenantId === 'string' &&
      typeof parsed.expiresAt === 'number' &&
      Number.isFinite(parsed.expiresAt) &&
      parsed.expiresAt > Date.now() &&
      isValidUser(parsed.user) &&
      parsed.user.tenantId === parsed.tenantId;

    if (!valid) {
      clearPortalSession();
      return null;
    }

    return parsed as StoredPortalSession;
  } catch {
    clearPortalSession();
    return null;
  }
}

export function clearPortalSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // No-op: no hay storage disponible para limpiar.
  }
}

